import { z } from 'zod';
import { asRecord } from '../../resultValues.js';
import { elementSchema, words } from '../brief/briefResult.js';
import { storyboardVersionSchema } from '../storyboard/storyboardResult.js';

const playbackUnavailableReasonSchema = z.enum([
  'video_bucket_not_configured',
  'source_not_allowed',
  'signing_failed',
  'malformed_video_result',
]);
const videoSchema = z.object({
  version_name: z.string(),
  strategy: z.string(),
  status: z.enum(['done', 'failed']),
  video_url: z.string().nullable().default(null),
  duration_seconds: z.number().nullable().default(null),
  error: z.string().nullable().default(null),
  warnings: words,
  playback_url: z.string().nullable().default(null),
  playback_unavailable_reason: playbackUnavailableReasonSchema.optional(),
  version: storyboardVersionSchema.optional(),
});
export const videoResultSchema = z.object({
  summary: z.string().nullable().optional(),
  videos: z.array(z.unknown()).transform((entries) => entries.map(parseVideoEntry)),
  storyboard: z
    .object({ elements: z.record(z.string(), elementSchema).catch({}) })
    .catch({ elements: {} }),
});

export type GeneratedVideo = z.infer<typeof videoResultSchema>['videos'][number];
export type GeneratedVideoResult = z.infer<typeof videoResultSchema>;
export type PlaybackUnavailableReason = z.infer<typeof playbackUnavailableReasonSchema>;

function parseVideoEntry(entry: unknown, index: number): z.infer<typeof videoSchema> {
  const parsed = videoSchema.safeParse(entry);
  if (!parsed.success) {
    const record = asRecord(entry);
    return {
      version_name:
        typeof record?.['version_name'] === 'string'
          ? record['version_name']
          : `Video ${index + 1}`,
      strategy: typeof record?.['strategy'] === 'string' ? record['strategy'] : 'unavailable',
      status: 'failed',
      video_url: null,
      duration_seconds: null,
      error: 'This video result could not be read.',
      warnings: [],
      playback_url: null,
      playback_unavailable_reason: 'malformed_video_result',
    };
  }
  if (parsed.data.video_url !== null && !parsed.data.video_url.startsWith('gs://')) {
    return {
      ...parsed.data,
      video_url: null,
      playback_url: null,
      playback_unavailable_reason: 'source_not_allowed',
    };
  }
  return parsed.data;
}

export function playableUrl(value: string | null): string | null {
  return value !== null && /^https?:\/\//i.test(value) ? value : null;
}

export function strategyLabel(video: GeneratedVideo): string {
  return video.version?.strategy_label || video.strategy;
}
